import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import useStack from 'hooks/useStack'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import TourV2DeparturesMonthsCalendar from './TourV2DeparturesMonthsCalendar'
import OfferCalendarCapacityChip from 'components/OfferPage/Filters/CalendarPicker/CalendarCapacity/OfferCalendarCapacityChip'
import TourV2DepartureTileList from './TourV2DepartureTileList'
import OfferCapacityWithAgesSelection from 'components/OfferPage/Filters/CapacityWithAges/OfferCapacityWithAgesSelection'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { addToCheckoutCart, initialiseCheckout } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import generateTourV2CheckoutItem from 'lib/tours/generateTourV2CheckoutItems'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import Group from 'components/utils/Group'
import OfferCalendarLuxPlusToggle from 'luxPlus/components/OfferCalendarLuxPlusToggle'
import { getMaxGuestCount, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { areDeparturesSoldOut, getDepartureCheapestOptionForTravellers, getDepartureOptionsForTravellers } from 'lib/tours/tourDepartureUtils'
import { getTourBundleRoomLimit, hasAvailableTourBundleRooms } from 'lib/tours/tourBundleRoomAvailability'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { generateOfferPageRoom, initialOfferPageRooms, OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { addToCartGTM, addTourToCart } from 'analytics/eventDefinitions'
import { addToCart } from 'analytics/snowplow/events'
import { fireEcommerceGTMEvent, addGTMEvent } from 'api/googleTagManager'
import { trackEvent } from 'analytics/analytics'
import { sum } from 'lib/array/arrayUtils'
import GeoContext from 'contexts/geoContext'
import { getCalendarDepartures, getCalendarDeparturesWithAnyBundleRooms } from 'selectors/TourV2/tourV2OfferPageSelectors'
import TourV2DeparturesSoldOutTile from './TourV2DeparturesSoldOutTile'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import TourPriceToggle from 'components/Tours/TourPriceToggle'
import { hasSelectedRooms, hasValidTourAdultAgesForFlightClassification } from 'lib/offer/offerPageUtil'
import { ALL_TOUR_PACKAGE_TYPE } from 'constants/tours'
import { getTourFlightsEnabled } from 'selectors/getTourFlightsEnabled'
import { useWeddingGiftRegistryContext } from 'weddings/contexts/WeddingGiftRegistryContext'
import useTourBundleUserCartItems from 'hooks/OfferPage/useTourBundleUserCartItems'

type Step = 'travellers' | 'departure' | 'month'
interface Props {
  offer: App.Tours.TourV2Offer
  travellers: Array<OfferPageRoom>
  defaultDate?: Date
  defaultDeparture?: App.Tours.TourV2OfferDeparture
  defaultStep?: Step
  calendarDepartures: Array<App.Tours.TourV2OfferDeparture>
  calendarDeparturesWithAnyBundleRooms: Array<App.Tours.TourV2OfferDeparture>
  variation: App.Tours.TourV2OfferVariation
  keepBrowsing?: boolean
  packageType?: string
}

function TourV2DepartureBookingModal(props: Props) {
  const {
    offer,
    travellers,
    defaultDate,
    defaultDeparture,
    defaultStep,
    variation,
    calendarDepartures,
    calendarDeparturesWithAnyBundleRooms,
    keepBrowsing = false,
    packageType,
  } = props

  const allDepartures = useMemo(() => variation.departures.filter(departure => hasAvailableTourBundleRooms(departure, travellers.length)), [variation.departures, travellers.length])
  const { currentCurrency } = useContext(GeoContext)
  const calcDefaultStep: Step = travellers === initialOfferPageRooms ? 'travellers' : 'departure'
  const [currentStep, goBack, nextStep] = useStack<Step>(defaultStep ?? calcDefaultStep)
  // Used to default the departures page to the correct scroll position
  const [defaultDepartureDate, setDefaultDepartureDate] = useState<Date | undefined>(defaultDate)
  const [selectedDeparture, setSelectedDeparture] = useState<App.Tours.TourV2OfferDeparture | undefined>(defaultDeparture)

  const intl = useIntl()
  const offerPageDispatch = useOfferPageDispatch()
  const dispatch = useAppDispatch()
  const tourFlightsEnabled = useAppSelector(getTourFlightsEnabled)
  const { generateUserCartItems } = useTourBundleUserCartItems(offer)
  const { resolve } = useModalElementContext()
  const { isActive: isWeddingGiftRegistry, addItems: addItemsToWeddingGiftRegistryCart } = useWeddingGiftRegistryContext()

  useEffect(() => {
    if (!currentStep) {
      // we 'popped' to the end of the list...means we close
      resolve()
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep])

  useEffect(() => {
    // Open the capacity filter when on travellers step, close it otherwise
    if (currentStep === 'travellers') {
      offerPageDispatch({ type: OfferPageActions.toggleHeaderFilter, filter: 'capacity' })
    } else {
      offerPageDispatch({ type: OfferPageActions.closeHeaderFilter })
    }
  }, [currentStep, offerPageDispatch])

  const checkoutDeparture = useCallback((departure: App.Tours.TourV2OfferDeparture) => {
    if (!hasValidTourAdultAgesForFlightClassification(travellers, offer, tourFlightsEnabled, variation)) return
    const items = travellers.map(traveller => {
      const resolvedPackageType = packageType && packageType !== ALL_TOUR_PACKAGE_TYPE ? packageType : undefined
      const option = getDepartureCheapestOptionForTravellers(departure, traveller, resolvedPackageType)
        ?? getDepartureCheapestOptionForTravellers(departure, traveller)
      if (option) {
        return generateTourV2CheckoutItem(
          offer,
          departure,
          traveller,
          option,
        )
      }
    })

    if (items.every(item => !!item)) {
      const hasBundledItems = items.some(item => !!item.ltleBundleId)
      const userCartItems = hasBundledItems ? generateUserCartItems(items) : undefined
      if (hasBundledItems && !userCartItems) return
      if (isWeddingGiftRegistry) {
        addItemsToWeddingGiftRegistryCart(userCartItems ?? items)
      } else if (keepBrowsing) {
        dispatch(addToCheckoutCart(userCartItems ?? items, undefined, userCartItems))
        showSnackbar(intl.formatMessage({
          id: 'UubO7eCopUaQyvXoh6mS',
          defaultMessage: 'Your item has been added to the cart.',
          description: 'Snackbar message after adding tour to cart on the tour page',
        }), 'success', {
          action: {
            label: intl.formatMessage({
              id: '7au9gwzUVBBIuuLo5ocg',
              defaultMessage: 'View cart',
              description: 'Snackbar action link to view shopping cart in the cart view',
            }),
            to: '/cart',
          },
        })
      } else {
        dispatch(initialiseCheckout(userCartItems ?? items, undefined, false, userCartItems))
        dispatch(navigateToCheckout())
      }

      const totalTravellers = sumUpOccupancies(travellers)
      const priceTotal = sum(items, item => item.total ?? 0)
      fireEcommerceGTMEvent(addToCartGTM(offer, currentCurrency, priceTotal, totalTravellers, 'tour'))
      addGTMEvent(addTourToCart(offer, currentCurrency, priceTotal, totalTravellers))
      trackEvent(addToCart())

      resolve()
    }
  }, [currentCurrency, dispatch, intl, offer, resolve, travellers, keepBrowsing, packageType, isWeddingGiftRegistry, addItemsToWeddingGiftRegistryCart, variation, tourFlightsEnabled, generateUserCartItems])

  const onTravellersSubmit = useCallback<React.FormEventHandler<HTMLFormElement>>(e => {
    e.preventDefault()
    // Close the capacity filter when submitting
    offerPageDispatch({ type: OfferPageActions.closeHeaderFilter })
    if (!hasSelectedRooms(travellers)) {
      offerPageDispatch({ type: OfferPageActions.setRooms, rooms: travellers.map(room => generateOfferPageRoom(room)) })
    }
    if (selectedDeparture) {
      // previously selected a departure, means the user was validating travellers
      const resolvedPackageType = packageType && packageType !== ALL_TOUR_PACKAGE_TYPE ? packageType : undefined
      const filteredOptions = resolvedPackageType
        ? getDepartureOptionsForTravellers(selectedDeparture, travellers, resolvedPackageType)
        : []
      const options = filteredOptions.length ? filteredOptions : getDepartureOptionsForTravellers(selectedDeparture, travellers)
      if (options.length) {
        checkoutDeparture(selectedDeparture)
      } else {
        // not valid, send them back to departure step to pick another one
        nextStep('departure')
      }
    } else {
      nextStep('departure')
    }
  }, [checkoutDeparture, nextStep, offerPageDispatch, selectedDeparture, travellers, packageType])

  const onCapacityFilterSelect = useCallback(() => {
    nextStep('travellers')
  }, [nextStep])

  const onYearSelect = useCallback(() => {
    nextStep('month')
  }, [nextStep])

  const onDepartureSelect = useCallback((departure: App.Tours.TourV2OfferDeparture) => {
    if (!hasSelectedRooms(travellers) || !hasValidTourAdultAgesForFlightClassification(travellers, offer, tourFlightsEnabled, variation)) {
      // this means the user hasn't explicitly picked their travellers yet
      // so force them to consider it by taking them there
      setSelectedDeparture(departure)
      setDefaultDepartureDate(new Date(departure.startDate))
      nextStep('travellers')
      return
    }

    const resolvedPackageType = packageType && packageType !== ALL_TOUR_PACKAGE_TYPE ? packageType : undefined
    const filteredOptions = resolvedPackageType
      ? getDepartureOptionsForTravellers(departure, travellers, resolvedPackageType)
      : []
    const options = filteredOptions.length ? filteredOptions : getDepartureOptionsForTravellers(departure, travellers)
    if (options.length) {
      checkoutDeparture(departure)
    }
    // somehow selected a departure not for sale?
  }, [checkoutDeparture, nextStep, travellers, packageType, offer, variation, tourFlightsEnabled])

  const onMonthSelect = useCallback((selectedDate: Date) => {
    setDefaultDepartureDate(selectedDate)
    nextStep('departure')
  }, [nextStep])

  const maxCapacityLabel = useMemo(() => {
    const maxTravellers = getMaxGuestCount(offer.roomCapacity)
    return intl.formatMessage(
      {
        id: 'mWqrZfcbH8XRRizglTFl',
        defaultMessage: '{maxTravellers, plural, one {Maximum capacity per room is # traveller} other {Maximum capacity per room is # travellers}}',
        description: 'Subtitle showing maximum room capacity for tour booking',
      },
      { maxTravellers },
    )
  }, [offer.roomCapacity, intl])

  const isInvalidDeparture = useMemo(() => {
    // We want to let the user know when they select a departure, then fills in their
    // travellers, but the departure they selected is not available for their travellers
    return selectedDeparture && getDepartureOptionsForTravellers(selectedDeparture, travellers).length > 0
  }, [selectedDeparture, travellers])

  const soldOutReason = useMemo(() => areDeparturesSoldOut(calendarDeparturesWithAnyBundleRooms, travellers), [calendarDeparturesWithAnyBundleRooms, travellers])

  return <ModalBase mode="drawer">
    {currentStep === 'travellers' && <form onSubmit={onTravellersSubmit}>
      <ModalHeader
        title={intl.formatMessage({
          id: 'fmxKHlprCA4cWm78unnY',
          defaultMessage: 'Travellers',
          description: 'Modal title for travellers selection step in the traveller form',
        })}
        subtitle={maxCapacityLabel}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          <OfferCapacityWithAgesSelection
            capacities={offer.roomCapacity}
            maxRooms={getTourBundleRoomLimit(selectedDeparture, 5)}
          />
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: intl.formatMessage({
            id: 'hEwvjQ_PYCIkSVZr_yep',
            defaultMessage: 'Continue',
            description: 'Button text to continue to next step in tour booking',
          }),
          type: 'submit',
        }}
      />
    </form>}
    {currentStep === 'departure' && <>
      <ModalHeader
        title={intl.formatMessage({
          id: '2jAzQJEOpMIKUo1VtAHL',
          defaultMessage: 'Select departure',
          description: 'Modal title for departure selection step dialog window',
        })}
        onBackButtonClick={goBack}
      >
        <Group gap={4} direction="horizontal">
          <OfferCalendarCapacityChip
            onClick={onCapacityFilterSelect}
            offer={offer}
          />
          <TourPriceToggle />
        </Group>
      </ModalHeader>
      <ModalBody>
        {isInvalidDeparture && <MessageBanner kind="warning" density="compact">
          {intl.formatMessage({
            id: 'q6AJIQQEVn1al_75SHyk',
            defaultMessage: 'Your selected departure is sold out for single rooms. Please select an alternate date.',
            description: 'Warning message when selected departure is sold out for single rooms',
          })}
        </MessageBanner>}
        {!!soldOutReason && <TourV2DeparturesSoldOutTile
          reason={soldOutReason}
          onUpdateTravelers={onCapacityFilterSelect}
        />}
        {!soldOutReason && <TourV2DepartureTileList
          offer={offer}
          onYearSelect={onYearSelect}
          onDepartureSelect={onDepartureSelect}
          defaultDate={defaultDepartureDate}
          departures={allDepartures}
          travellers={travellers}
          variation={variation}
          packageType={packageType}
        />}
      </ModalBody>
    </>}
    {currentStep === 'month' && <>
      <ModalHeader
        title={intl.formatMessage({
          id: 'kNNvhkv-vjyXESfDNff-',
          defaultMessage: 'Select a month',
          description: 'Modal title for month selection step in tour booking',
        })}
        onBackButtonClick={goBack}
      >
        <Group direction="horizontal" gap={8} wrap="wrap">
          <OfferCalendarCapacityChip
            onClick={onCapacityFilterSelect}
            offer={offer}
          />
          {offer.luxPlus.hasMemberPrices && <OfferCalendarLuxPlusToggle />}
        </Group>
      </ModalHeader>
      <ModalBody>
        <ModalContent>
          {soldOutReason && <TourV2DeparturesSoldOutTile
            reason={soldOutReason}
            onUpdateTravelers={onCapacityFilterSelect}
          />}
          {!soldOutReason && <TourV2DeparturesMonthsCalendar
            onSelect={onMonthSelect}
            offer={offer}
            travellers={travellers}
            departures={calendarDepartures}
          />}
        </ModalContent>
      </ModalBody>
    </>}
  </ModalBase>
}

type MappedProps = Pick<Props, 'travellers' | 'calendarDepartures' | 'calendarDeparturesWithAnyBundleRooms' | 'variation'>

export default withOfferPageState((state): MappedProps => ({
  travellers: state.rooms,
  calendarDepartures: getCalendarDepartures(state),
  calendarDeparturesWithAnyBundleRooms: getCalendarDeparturesWithAnyBundleRooms(state),
  variation: state.tourV2Variation!,
}), TourV2DepartureBookingModal)
